import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Gallery.css";

export default function GalleryFull() {
  const [data, setData] = useState({ title: "Gallery", items: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(-1);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [viewMode, setViewMode] = useState("comfort");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/content/gallery.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json || { items: [] });
      } catch (e) {
        console.error("Failed to load gallery.json", e);
        if (!cancelled) setErr("Couldn’t load gallery. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const photos = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data?.items]);
  const tags = useMemo(() => {
    const unique = new Set();
    photos.forEach((item) => {
      (item.tags || []).forEach((tag) => unique.add(tag));
    });
    return ["All", ...Array.from(unique).sort()];
  }, [photos]);

  const visiblePhotos = useMemo(() => {
    const term = query.trim().toLowerCase();
    return photos.filter((item) => {
      const itemTags = item.tags || [];
      const matchesTag = activeTag === "All" || itemTags.includes(activeTag);
      const matchesFeatured = !featuredOnly || item.featured;
      const searchable = [
        item.caption,
        item.alt,
        item.image,
        ...itemTags,
      ].filter(Boolean).join(" ").toLowerCase();
      const matchesQuery = !term || searchable.includes(term);
      return matchesTag && matchesFeatured && matchesQuery;
    });
  }, [activeTag, featuredOnly, photos, query]);

  const isOpen = idx > -1;
  const close = useCallback(() => setIdx(-1), []);
  const next = useCallback(() => setIdx((i) => (i + 1) % visiblePhotos.length), [visiblePhotos.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + visiblePhotos.length) % visiblePhotos.length), [visiblePhotos.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, isOpen, next, prev, visiblePhotos.length]);

  useEffect(() => {
    setIdx(-1);
  }, [activeTag, featuredOnly, query]);

  return (
    <div className="gf-page">
      {/* Lightweight sticky header (no site Navbar) */}
      <header className="gf-topbar">
        <div className="gf-topbar__inner">
          <Link to="/" className="gf-brand" aria-label="Go to home">Parivar Wedding Event</Link>
          <nav className="gf-topbar__nav" aria-label="Gallery navigation">
            <Link to="/" className="gf-topbar__link">Home</Link>
            <a href="#gallery-grid" className="gf-topbar__link">Photos</a>
          </nav>
        </div>
      </header>

      <main className="content">
        <section className="gf-hero">
          <div>
            <p className="gf-kicker">Wedding Stories</p>
            <h1 className="gf-title">{data?.title || "Gallery"}</h1>
            <p className="gf-subtitle">
              A curated collection of destination weddings, decor details, and celebration moments.
            </p>
          </div>

          <div className="gf-stats" aria-label="Gallery statistics">
            <div>
              <strong>{photos.length}</strong>
              <span>Total Photos</span>
            </div>
            <div>
              <strong>{photos.filter((item) => item.featured).length}</strong>
              <span>Featured</span>
            </div>
            <div>
              <strong>{Math.max(tags.length - 1, 0)}</strong>
              <span>Categories</span>
            </div>
          </div>
        </section>

        <section className="gal-section gf-gallery-section">
          <div className="gf-toolbar">
            <label className="gf-search">
              <span>Search gallery</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by caption, tag, or image"
              />
            </label>

            <div className="gf-actions">
              <button
                type="button"
                className={`gf-filter-btn ${featuredOnly ? "active" : ""}`}
                onClick={() => setFeaturedOnly((value) => !value)}
              >
                Featured
              </button>
              <button
                type="button"
                className={`gf-filter-btn ${viewMode === "comfort" ? "active" : ""}`}
                onClick={() => setViewMode("comfort")}
              >
                Comfort
              </button>
              <button
                type="button"
                className={`gf-filter-btn ${viewMode === "compact" ? "active" : ""}`}
                onClick={() => setViewMode("compact")}
              >
                Compact
              </button>
            </div>
          </div>

          {tags.length > 1 && (
            <div className="gf-tags" aria-label="Gallery filters">
              {tags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  className={`gf-tag ${activeTag === tag ? "active" : ""}`}
                  onClick={() => setActiveTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className={`gf-grid gf-grid--${viewMode}`} id="gallery-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div className="gf-item skeleton" key={i} style={{ height: 220 + ((i * 37) % 120) }} />
              ))}
            </div>
          )}

          {err && <div className="text-center text-danger mb-4">{err}</div>}

          {!loading && !err && photos.length === 0 && (
            <div className="gf-empty">No photos yet.</div>
          )}

          {!loading && !err && photos.length > 0 && visiblePhotos.length === 0 && (
            <div className="gf-empty">No photos match the selected filters.</div>
          )}

          <div className={`gf-grid gf-grid--${viewMode}`} id="gallery-grid">
            {visiblePhotos.map((item, i) => (
              <figure className="gf-item" key={`${item.image}-${i}`} onClick={() => setIdx(i)}>
                <img
                  src={item.image} // works with /uploads/... or full URLs
                  alt={item.alt || item.caption || `Photo ${i + 1}`}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
                />
                <span className="gf-zoom">View</span>
                {(item.caption || item.tags?.length || item.featured) && (
                  <figcaption className="caption">
                    {item.caption && <span className="cap">{item.caption}</span>}
                    {item.tags?.length ? (
                      <span className="tags">
                        {item.tags.map((t, k) => <em key={k}>#{t}</em>)}
                      </span>
                    ) : null}
                    {item.featured && <span className="gf-featured">Featured</span>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      </main>

      {/* Simple local footer to keep page self-contained */}
      <footer className="gf-footer">© {new Date().getFullYear()} Parivar</footer>

      {isOpen && visiblePhotos[idx] && (
        <div className="lightbox" onClick={close}>
          <button className="lb-btn lb-close" aria-label="Close" onClick={close}>×</button>
          {visiblePhotos.length > 1 && (
            <>
              <button className="lb-btn lb-prev" aria-label="Previous"
                onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>
              <button className="lb-btn lb-next" aria-label="Next"
                onClick={(e) => { e.stopPropagation(); next(); }}>›</button>
            </>
          )}
          <img
            className="lb-img"
            src={visiblePhotos[idx].image}
            alt={visiblePhotos[idx].alt || visiblePhotos[idx].caption || "Photo"}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="lb-counter">{idx + 1} / {visiblePhotos.length}</div>
          {(visiblePhotos[idx].caption || visiblePhotos[idx].tags?.length || visiblePhotos[idx].featured) && (
            <div className="lb-meta" onClick={(e) => e.stopPropagation()}>
              {visiblePhotos[idx].caption && <div>{visiblePhotos[idx].caption}</div>}
              {visiblePhotos[idx].featured && <strong>Featured</strong>}
              {visiblePhotos[idx].tags?.length ? (
                <div className="lb-tags">
                  {visiblePhotos[idx].tags.map((t, k) => <em key={k}>#{t}</em>)}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
